import React, { useState, useEffect } from 'react';


import { notificationPreferencesService } from '../../../services/emailAlertService';
import Icon from '../../../components/AppIcon';


const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    profileViewAlerts: true,
    clubInterestAlerts: true,
    transferInquiryAlerts: true,
    marketOpportunityAlerts: true,
    weeklyDigest: false,
    monthlyReport: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const notificationItems = [
    {
      id: 'emailNotifications',
      label: 'Email Notifications',
      description: 'Receive notifications via email',
      icon: 'Mail',
      color: 'bg-blue-500/10 text-blue-500'
    },
    {
      id: 'smsNotifications',
      label: 'SMS Notifications',
      description: 'Receive notifications via SMS',
      icon: 'MessageSquare',
      color: 'bg-green-500/10 text-green-500'
    },
    {
      id: 'pushNotifications',
      label: 'Push Notifications',
      description: 'Receive push notifications in your browser',
      icon: 'Bell',
      color: 'bg-purple-500/10 text-purple-500'
    },
    {
      id: 'profileViewAlerts',
      label: 'Profile View Alerts',
      description: 'Get notified when someone views your profile',
      icon: 'Eye',
      color: 'bg-orange-500/10 text-orange-500'
    },
    {
      id: 'clubInterestAlerts',
      label: 'Club Interest Alerts',
      description: 'Receive alerts when clubs show interest',
      icon: 'Users',
      color: 'bg-indigo-500/10 text-indigo-500'
    },
    {
      id: 'transferInquiryAlerts',
      label: 'Transfer Inquiry Alerts',
      description: 'Get notified about transfer inquiries',
      icon: 'ArrowRightLeft',
      color: 'bg-pink-500/10 text-pink-500'
    },
    {
      id: 'marketOpportunityAlerts',
      label: 'Market Opportunity Alerts',
      description: 'Receive alerts about market opportunities',
      icon: 'TrendingUp',
      color: 'bg-cyan-500/10 text-cyan-500'
    },
    {
      id: 'weeklyDigest',
      label: 'Weekly Digest',
      description: 'Receive a weekly summary of your activity',
      icon: 'Calendar',
      color: 'bg-yellow-500/10 text-yellow-500'
    },
    {
      id: 'monthlyReport',
      label: 'Monthly Report',
      description: 'Receive a monthly performance report',
      icon: 'FileText',
      color: 'bg-red-500/10 text-red-500'
    }
  ];

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await notificationPreferencesService?.get();
      if (response?.data) {
        setSettings(response?.data);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      setMessage({ type: 'error', text: 'Failed to load preferences' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await notificationPreferencesService?.update(settings);
      setMessage({ type: 'success', text: 'Preferences saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({ type: 'error', text: 'Failed to save preferences' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      profileViewAlerts: true,
      clubInterestAlerts: true,
      transferInquiryAlerts: true,
      marketOpportunityAlerts: true,
      weeklyDigest: false,
      monthlyReport: true
    });
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground font-headline flex items-center gap-2">
            <Icon name="Bell" size={24} className="text-primary" />
            Notification Settings
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Manage how you receive notifications</p>
        </div>
      </div>
      <div className="space-y-4">
        {notificationItems?.map((item) => (
          <div
            key={item?.id}
            className="flex items-start justify-between gap-4 p-4 rounded-lg border border-border hover:bg-muted transition-colors duration-300"
          >
            <div className="flex items-start gap-3 flex-1">
              <div className={`w-10 h-10 rounded-lg ${item?.color} flex items-center justify-center flex-shrink-0`}>
                <Icon name={item?.icon} size={20} />
              </div>
              <div>
                <p className="text-base font-semibold text-foreground mb-1">{item?.label}</p>
                <p className="text-sm text-muted-foreground">{item?.description}</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.[item?.id]}
                onChange={() => handleToggle(item?.id)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationSettings;